import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiMenu, FiX, FiActivity, FiArrowUpRight } from 'react-icons/fi'

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState('home')

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
      const sections = ['home', 'heritage', 'missions', 'volunteers', 'gallery', 'impact']
      for (const section of sections) {
        const element = document.getElementById(section)
        if (element) {
          const rect = element.getBoundingClientRect()
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section)
            break
          }
        }
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (id) => {
    const element = document.getElementById(id)
    if (element) {
      const offset = 100
      const bodyRect = document.body.getBoundingClientRect().top
      const elementRect = element.getBoundingClientRect().top
      const elementPosition = elementRect - bodyRect
      const offsetPosition = elementPosition - offset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
      setIsOpen(false)
    }
  }

  const navItems = [
    { label: 'HOME', id: 'home' },
    { label: 'LEGACY', id: 'heritage' },
    { label: 'MISSIONS', id: 'missions' },
    { label: 'CREW', id: 'volunteers' },
    { label: 'IMPACT', id: 'impact' },
  ]

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-6 left-0 right-0 z-50 px-4 md:px-0 transition-all duration-500`}
      >
        <div className={`max-w-5xl mx-auto flex items-center justify-between px-8 py-4 rounded-[2rem] transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/60 backdrop-blur-2xl border border-gray-200 shadow-[0_20px_50px_rgba(0,0,0,0.1)]' 
            : 'bg-transparent border border-transparent'
        }`}>
          {/* Logo Section */}
          <div 
            onClick={() => scrollToSection('home')}
            className="flex items-center space-x-3 cursor-pointer group"
          >
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-accent-maroon/10 overflow-hidden flex items-center justify-center border-2 border-accent-maroon/30 group-hover:scale-110 transition-transform duration-500">
                <img src="/logo.png" alt="Mission Mewar Samriddhi Logo" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white animate-pulse" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-lg font-black font-grotesk tracking-tight text-gray-800 uppercase">Mewar</span>
              <span className="text-[7px] font-black tracking-[0.45em] text-accent-maroon uppercase">Samriddhi</span>
            </div>
          </div>

          {/* Nav Links - Desktop */}
          <div className="hidden lg:flex items-center space-x-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`relative px-5 py-2 text-[10px] font-black tracking-[0.2em] transition-all duration-300 rounded-full group ${
                  activeSection === item.id ? 'text-gray-800' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <span className="relative z-10">{item.label}</span>
                {activeSection === item.id && (
                  <motion.div
                    layoutId="activePill"
                    className="absolute inset-0 bg-gray-100 border border-gray-200 rounded-full"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-6">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('volunteers')}
              className="hidden md:flex items-center space-x-2 px-6 py-2.5 bg-accent-maroon text-white font-black rounded-full text-[10px] uppercase tracking-widest transition-all shadow-[0_10px_30px_rgba(128,0,0,0.2)]"
            >
              <span>CONTRIBUTE</span>
              <FiArrowUpRight />
            </motion.button>

            {/* Mobile Toggle */}
            <button 
              onClick={() => setIsOpen(true)}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 lg:hidden transition-colors"
            >
              <FiMenu size={20} />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Fullscreen Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[60] bg-white/95 backdrop-blur-3xl lg:hidden p-6 sm:p-8 md:p-12 flex flex-col justify-between"
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-accent-maroon/30">
                  <img src="/logo.png" alt="Mission Mewar Samriddhi Logo" className="w-full h-full object-cover" />
                </div>
                <span className="text-lg sm:text-xl font-black text-gray-800 uppercase tracking-tighter font-grotesk">MEWAR SAMRIDDHI</span>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-800 border border-gray-200"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="flex flex-col gap-4 sm:gap-6 md:gap-8">
              {navItems.map((item, i) => (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => scrollToSection(item.id)}
                  className="text-3xl sm:text-4xl md:text-6xl font-black text-gray-800 text-left tracking-tighter hover:text-accent-maroon transition-colors"
                >
                  {item.label}
                </motion.button>
              ))}
            </div>

            <div className="space-y-4 sm:space-y-6 md:space-y-8">
              <div className="h-px bg-gray-200" />
              <button 
                onClick={() => scrollToSection('volunteers')}
                className="w-full py-4 sm:py-6 md:py-8 bg-accent-maroon text-white rounded-lg sm:rounded-xl md:rounded-[2rem] font-black text-sm sm:text-lg md:text-xl uppercase tracking-widest shadow-2xl"
              >
                JOIN THE CREW
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Navbar
